import { ErrorHandler, Injectable } from '@angular/core';
import { clearBrowserCachesAndReload } from './shared/utils/clear-browser-caches';

@Injectable()
export class AppErrorHandler implements ErrorHandler {

    private readonly RELOAD_KEY = 'chunk_error_reload_ts';
    private readonly RELOAD_COUNT_KEY = 'chunk_error_reload_count';
    private readonly RELOAD_BLOCK_WINDOW_MS = 2 * 60 * 1000;
    private readonly RELOAD_COUNT_WINDOW_MS = 60 * 60 * 1000;
    private readonly RELOAD_MAX_ATTEMPTS = 3;

    private readonly FIRESTORE_RELOAD_KEY = 'firestore_reload_ts';
    private readonly FIRESTORE_RELOAD_BLOCK_WINDOW_MS = 30 * 1000;

    handleError(error: unknown): void {
        if (this.isChunkError(error) && !this.hasReloadBeenAttempted()) {
            this.markReloadAttempted();
            clearBrowserCachesAndReload();
            return;
        }

        if (this.isFirestoreAssertionError(error) && !this.hasFirestoreReloadBeenAttempted()) {
            console.error('Firestore SDK internal assertion detected, reloading page...', error);
            this.markFirestoreReloadAttempted();
            window.location.reload();
            return;
        }

        console.error(error);
    }

    private markReloadAttempted(): void {
        sessionStorage.setItem(this.RELOAD_KEY, Date.now().toString());
        sessionStorage.setItem(this.RELOAD_COUNT_KEY, (this.getReloadCount() + 1).toString());
    }

    private hasReloadBeenAttempted(): boolean {
        if (this.getReloadCount() >= this.RELOAD_MAX_ATTEMPTS) {
            return true;
        }

        const raw = sessionStorage.getItem(this.RELOAD_KEY);

        if (!raw) {
            return false;
        }

        return Date.now() - Number(raw) < this.RELOAD_BLOCK_WINDOW_MS;
    }

    private getReloadCount(): number {
        if (this.isCountWindowExpired()) {
            return 0;
        }

        return Number(sessionStorage.getItem(this.RELOAD_COUNT_KEY)) || 0;
    }

    private isCountWindowExpired(): boolean {
        const lastReloadTimestamp = sessionStorage.getItem(this.RELOAD_KEY);

        if (!lastReloadTimestamp) {
            return true;
        }

        const elapsed = Date.now() - Number(lastReloadTimestamp);

        return elapsed >= this.RELOAD_COUNT_WINDOW_MS;
    }

    private isChunkError(error: unknown): boolean {
        if (!(error instanceof Error)) {
            return false;
        }

        return (
            error.name === 'ChunkLoadError' ||
            /Loading chunk [\w-]+ failed/i.test(error.message) ||
            /Failed to fetch dynamically imported module/i.test(error.message) ||
            /Importing a module script failed/i.test(error.message)
        );
    }

    private isFirestoreAssertionError(error: unknown): boolean {
        const message = error instanceof Error
            ? error.message
            : String(error);

        return /INTERNAL ASSERTION FAILED: Unexpected state/i.test(message);
    }

    private markFirestoreReloadAttempted(): void {
        sessionStorage.setItem(this.FIRESTORE_RELOAD_KEY, Date.now().toString());
    }

    private hasFirestoreReloadBeenAttempted(): boolean {
        const raw = sessionStorage.getItem(this.FIRESTORE_RELOAD_KEY);

        if (!raw) {
            return false;
        }

        return Date.now() - Number(raw) < this.FIRESTORE_RELOAD_BLOCK_WINDOW_MS;
    }
}
